import { useMemo, useState } from 'react';

import {
  PlayCircleFilled,
  AccountCircle,
  MenuOpen,
  Folder,
  Campaign
} from '@mui/icons-material';
import { Grid, Stack, Typography } from '@mui/material';
import {
  AxisConfig,
  BarChart,
  ChartsYAxisProps,
  PieChart
} from '@mui/x-charts';

import { useQueryClient } from '@tanstack/react-query';

import Alert from '@/components/alert';
import Button from '@/components/button';
import Checkbox from '@/components/checkbox';
import ResultCard from '@/components/resultCard';
import TraceIdDisplay from '@/components/traceIdDisplay';
import { VISUAL_RECOGNITION_FEATURE } from '@/constants/features';
import { PreviewResponse } from '@/hooks/queries/evaluations';
import { useAppSelector } from '@/hooks/redux';
import { Workspace } from '@/models/workspace';

import ChartCard from '../../statistics/_partials/ChartCard';
import ExpandableLegend from '../../statistics/_partials/ExpandableLegend';

interface Props {
  results: PreviewResponse;
  isLoading: boolean;
  error: boolean;
  onChange?: (field: string, value: any) => void;
  requestedServices?: Array<any>;
  isDisabled?: boolean;
}

const PLATFORM_COLOR_MAP: Record<string, string> = {
  youtube: '#EA5455',
  instagram: '#FFB44F',
  tiktok: '#326CCB'
};

const TYPE_COLOR_MAP: Record<string, string> = {
  'instagram-post': '#FF9200',
  'instagram-reel': '#AE8F67',
  'instagram-story': '#B26600',
  'tiktok-post': '#4A9FE8',
  'tiktok-story': '#6254C7',
  'youtube-video': '#E74A2E',
  'youtube-short': '#921B1C',
  'youtube-livestream': '#22C55E'
};

const safeValue = (value: number | undefined, fallback = 0) =>
  value ?? fallback;

const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

interface PlatformStats {
  totalPosts: number;
  postTypes: Record<string, number>;
}

const useChartData = (results: PreviewResponse, keywordSlice?: number) =>
  useMemo(() => {
    const platformsData = (results.platforms ?? {}) as Record<
      string,
      PlatformStats
    >;
    const platformEntries = Object.entries(platformsData);

    const platforms = platformEntries.map(([key, data]) => ({
      label: capitalize(key),
      value: safeValue(data.totalPosts),
      color: PLATFORM_COLOR_MAP[key.toLowerCase()]
    }));

    const types = platformEntries.flatMap(([key, data]) =>
      Object.entries(data.postTypes).map(([typeKey, value]) => ({
        label: capitalize(typeKey),
        value: safeValue(value),
        key: capitalize(key),
        color: TYPE_COLOR_MAP[`${key.toLowerCase()}-${typeKey.toLowerCase()}`]
      }))
    );

    const allKeywords = [...(results.keywordOccurrences ?? [])].sort(
      (a, b) => b.matchedPosts - a.matchedPosts
    );
    const keywordsToShow = allKeywords.slice(0, keywordSlice);

    return { platforms, types, allKeywords, keywordsToShow };
  }, [results, keywordSlice]);

const Step4 = ({
  error,
  results,
  isLoading,
  onChange,
  isDisabled,
  requestedServices
}: Props): React.JSX.Element => {
  const [keywordSlice, setKeywordSlice] = useState<number | undefined>(7);
  const queryClient = useQueryClient();

  const hasVRForMinorsSelected = requestedServices?.some(
    (service) => service.type === 'minors'
  );

  const user = useAppSelector((state) => state.user.currentUser);
  const workspaces = queryClient.getQueryData(['workspaces']) as Workspace[];
  const workspace = workspaces?.find(
    (w: Workspace) => w.id === user?.workspaceId
  );
  const IS_VR_ENABLED = workspace
    ? workspace.features.includes(VISUAL_RECOGNITION_FEATURE)
    : false;

  const { platforms, types, keywordsToShow } = useChartData(
    results,
    keywordSlice
  );

  const keywordsHeight = useMemo(
    () => Math.max((keywordSlice || keywordsToShow.length) * 40, 300),
    [keywordsToShow, keywordSlice]
  );

  const keywordsMarginLeft = useMemo(() => {
    if (!keywordsToShow.length) return 100;
    const longestKeywordLength = Math.max(
      ...keywordsToShow.map((item) => item.keyword.length)
    );
    return 100 + longestKeywordLength * 5;
  }, [keywordsToShow]);

  const showCharts = !error && !isLoading;
  const iconColor = error ? 'disabled' : 'primary';

  const summaryItems = [
    { Icon: PlayCircleFilled, label: `${results.totalPosts} Posts` },
    { Icon: AccountCircle, label: `${results.totalCreators} Creators` },
    { Icon: MenuOpen, label: `${results.totalKeywords} Keywords` },
    { Icon: Folder, label: `${results.totalKeywordGroups} Keyword Groups` },
    { Icon: Campaign, label: `${results.sponsoredPosts} Sponsored` }
  ];

  return (
    <Stack gap={2} pb={2}>
      {!error && !isLoading && IS_VR_ENABLED && (
        <Alert
          id="request-detection-minors"
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
            opacity: isDisabled ? 0.5 : 1,
            pointerEvents: isDisabled ? 'none' : 'auto'
          }}
          severity="info"
          content="Request detection of minors <b>(below 18 years old)</b> for this evaluation."
          action={
            <Checkbox
              checked={hasVRForMinorsSelected}
              disabled={isDisabled}
              color="info"
              onChange={(event) =>
                onChange?.(
                  'requestedServices',
                  event.target.checked ? ['minors'] : []
                )
              }
            />
          }
        />
      )}
      <ResultCard
        disabled={error}
        items={summaryItems.map(({ Icon, label }) => ({
          icon: <Icon fontSize="large" color={iconColor} />,
          label,
          loading: isLoading,
          title: 'Includes:'
        }))}
      />
      {showCharts && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ChartCard
              title="Posts per platform"
              disabled={!results.totalPosts}
              chart={
                <PieChart
                  series={[
                    {
                      data: results.totalPosts ? platforms : [],
                      id: 'platforms',
                      highlightScope: { fade: 'global', highlight: 'item' },
                      innerRadius: 0,
                      outerRadius: 80
                    },
                    {
                      data: results.totalPosts ? types : [],
                      id: 'types',
                      highlightScope: { fade: 'global', highlight: 'item' },
                      innerRadius: 100,
                      outerRadius: 120
                    }
                  ]}
                  margin={{ right: 5 }}
                  slotProps={{ legend: { hidden: true } }}
                  width={400}
                  height={300}
                />
              }
              legend={
                results.totalPosts ? (
                  <ExpandableLegend platforms={platforms} types={types} />
                ) : (
                  <></>
                )
              }
            />
          </Grid>
          {keywordsToShow.length > 0 && (
            <Grid item xs={12}>
              <ChartCard
                actions={
                  keywordSlice === undefined ? (
                    <Button
                      variant="outlined"
                      text="Show less"
                      onClick={() => setKeywordSlice(7)}
                    />
                  ) : (
                    <Button
                      variant="contained"
                      text="Show all"
                      onClick={() => setKeywordSlice(undefined)}
                    />
                  )
                }
                title="Occurrences per keywords"
                chart={
                  <BarChart
                    sx={{
                      border:
                        '1px solid var(--Border-Medium, rgba(28, 28, 28, 0.14))'
                    }}
                    dataset={keywordsToShow}
                    colors={['#000']}
                    xAxis={[{ tickMinStep: 1 }]}
                    yAxis={[
                      {
                        scaleType: 'band',
                        dataKey: 'keyword',
                        categoryGapRatio: 0.52
                      } as AxisConfig<'band', unknown, ChartsYAxisProps>
                    ]}
                    series={[{ dataKey: 'matchedPosts' }]}
                    layout="horizontal"
                    grid={{ vertical: true }}
                    margin={{ left: keywordsMarginLeft }}
                    height={keywordsHeight}
                  />
                }
              />
            </Grid>
          )}
        </Grid>
      )}
      {error && !isLoading && (
        <Alert
          severity="error"
          content={
            <Stack gap={1} alignItems="left">
              <Typography component="span" align="center">
                Sorry! We could not preview your evaluation because of an
                unknown error.
              </Typography>
              <TraceIdDisplay />
            </Stack>
          }
        />
      )}
    </Stack>
  );
};

export default Step4;
